import '../styles/PageGlobal.css';
import './FournisseurDetailsEditPage.css';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FournisseurService } from '../services/FournisseurService.js';
import { ContactService } from '../services/ContactService.js'; 

export default function FournisseurDetailsEditPage() {
  const { fournisseur_id } = useParams();
  const navigate = useNavigate();

  // 🟢 Initialisation des services avec useMemo
  const fournisseurService = useMemo(() => new FournisseurService(), []);
  const contactService = useMemo(() => new ContactService(), []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🟢 1. État simplifié : uniquement les champs modifiables du fournisseur
  const [form, setForm] = useState({
    societe1: '', societe2: '', email: '', telephone: '',
    kbis: '', siret: '', siren: '', tvaIntraComm: ''
  });

  // 🟢 2. États pour l'affichage en lecture seule des listes
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);

  const fetchFournisseurData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // A. Récupérer les données principales du fournisseur et ses adresses
      const data = await fournisseurService.getFournisseurById(fournisseur_id);
      
      setForm({
        societe1: data.societe1 || '',
        societe2: data.societe2 || '',
        email: data.email || '',
        telephone: data.telephone || '',
        kbis: data.kbis || '',
        siret: data.siret || '',
        siren: data.siren || '',
        tvaIntraComm: data.tvaIntraComm || data.tva || '' // Fallback au cas où le backend utilise 'tva'
      });
      
      setAddresses(data.addresses || []);

      // B. 🟢 NOUVEAU : Récupérer spécifiquement les contacts via le ContactService
      try {
        // Adaptez le nom de la méthode si votre ContactService utilise un autre nom (ex: getByFournisseurId)
        const contactsData = await contactService.getContactsByFournisseurId(fournisseur_id);
        setContacts(contactsData || []);
      } catch (err) {
        console.warn("Impossible de charger les contacts du fournisseur :", err);
        // Fallback : si le backend les inclut déjà dans getFournisseurById, on les utilise
        setContacts(data.contacts || []); 
      }
      
    } catch (err) {
      console.error("Erreur chargement fournisseur:", err);
      setError(err.message || "Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, [fournisseur_id, fournisseurService, contactService]);

  useEffect(() => { 
    fetchFournisseurData(); 
  }, [fetchFournisseurData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // 🟢 3. Payload épuré : on n'envoie PLUS les adresses ou contacts depuis cette page
      const payload = {
        societe1: form.societe1, 
        societe2: form.societe2, 
        email: form.email, 
        telephone: form.telephone,
        kbis: form.kbis, 
        siret: form.siret, 
        siren: form.siren,
        tvaIntraComm: form.tvaIntraComm
      };
      
      await fournisseurService.updateFournisseur(fournisseur_id, payload);
      alert("Fournisseur modifié avec succès !");
      navigate("/fournisseur_list");
    } catch (err) {
      console.error(err);
      setError(err.message || "Impossible de sauvegarder");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="containerMain"><p>Chargement des données…</p></div>;
  if (error) return <div className="containerMain"><p className="fournisseur-error" style={{color: 'red'}}>Erreur : {error}</p></div>;

  return (
    <div className="containerMain fournisseur-edit-container">
      <h1 className="fournisseur-edit-title">Fournisseur : Édition des informations principales</h1>
      
      <div className="fournisseur-edit-form">
        {/* --- SECTION MODIFIABLE --- */}
        <fieldset className="fournisseur-edit-section">
          <legend>Identité de l'entreprise</legend>
          <div className="fournisseur-edit-grid fournisseur-edit-grid-2">
            <div className="fournisseur-edit-row">
              <label>Société 1 *</label>
              <input name="societe1" value={form.societe1} onChange={handleChange} required />
            </div>
            <div className="fournisseur-edit-row">
              <label>Société 2</label>
              <input name="societe2" value={form.societe2} onChange={handleChange} />
            </div>
          </div>
        </fieldset>

        <fieldset className="fournisseur-edit-section">
          <legend>Contact principal</legend>
          <div className="fournisseur-edit-grid fournisseur-edit-grid-2">
            <div className="fournisseur-edit-row">
              <label>Email *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="fournisseur-edit-row">
              <label>Téléphone *</label>
              <input name="telephone" value={form.telephone} onChange={handleChange} required />
            </div>
          </div>
        </fieldset>

        <fieldset className="fournisseur-edit-section">
          <legend>Informations administratives</legend>
          <div className="fournisseur-edit-grid fournisseur-edit-grid-3">
            <div className="fournisseur-edit-row"><label>KBIS</label><input name="kbis" value={form.kbis} onChange={handleChange} /></div>
            <div className="fournisseur-edit-row"><label>SIRET</label><input name="siret" value={form.siret} onChange={handleChange} maxLength={14} /></div>
            <div className="fournisseur-edit-row"><label>SIREN</label><input name="siren" value={form.siren} onChange={handleChange} maxLength={9} /></div>
            <div className="fournisseur-edit-row"><label>TVA Intracommunautaire</label><input name="tvaIntraComm" value={form.tvaIntraComm} onChange={handleChange} /></div>
          </div>
        </fieldset>

        {/* --- SECTIONS EN LECTURE SEULE (LISTES) --- */}
        
        <fieldset className="fournisseur-edit-section" style={{ backgroundColor: '#f8f9fa' }}>
          <legend>📍 Adresses liées (Gérées séparément)</legend>
          {addresses.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucune adresse enregistrée pour ce fournisseur.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {addresses.map((addr, index) => (
                <li key={addr.id || index} style={{ 
                  padding: '12px', 
                  marginBottom: '8px', 
                  background: '#fff', 
                  border: '1px solid #ddd', 
                  borderRadius: '6px' 
                }}>
                  <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '4px' }}>
                    {addr.type === 'LIVRAISON' ? '🚚 Livraison' : 
                     addr.type === 'FACTURATION' ? '📄 Facturation' : 
                     addr.type === 'SIEGE_SOCIAL' ? '🏢 Siège Social' : '📍 Adresse'}
                  </div>
                  <div>{addr.adresse1} {addr.adresse2}</div>
                  <div>{addr.codePostal} {addr.ville}, {addr.pays}</div>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <fieldset className="fournisseur-edit-section" style={{ backgroundColor: '#f8f9fa' }}>
          <legend>👥 Contacts liés</legend>
          {contacts.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun contact secondaire enregistré pour ce fournisseur.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Nom</th>
                  <th style={{ padding: '8px' }}>Email</th>
                  <th style={{ padding: '8px' }}>Téléphone</th>
                  <th style={{ padding: '8px' }}>Fonction</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, index) => (
                  <tr key={contact.id || index} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{contact.nom || contact.name || 'N/A'}</td>
                    <td style={{ padding: '8px' }}>{contact.email || 'N/A'}</td>
                    <td style={{ padding: '8px' }}>{contact.tel || contact.telephone || 'N/A'}</td>
                    <td style={{ padding: '8px' }}>{contact.fonction || contact.role || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </fieldset>
      </div>

      <div className="fournisseur-edit-footer">
        <button className="fournisseur-btn fournisseur-btn-blue" onClick={handleSave} disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
        <button className="fournisseur-btn fournisseur-btn-grey" onClick={() => navigate("/fournisseur_list")}>
          Annuler
        </button>
      </div>
    </div>
  );
}